import { Injectable, Injector } from '@angular/core';
import { AppState } from '@app/reducers';
import { Store } from '@ngrx/store';
import { DialogService } from "primeng/api";
import { combineLatest } from 'rxjs';
import { AgentTelephonyProvider, ResponseMessageTypes } from "../enums/enum";
import { agentLoginDataSelector } from '../layouts/app-header/state/agent-login.selector';
import { ExtensionModalComponent } from "../layouts/app-header/extension-modal.component";
import { sidebarDataSelector$ } from '../layouts/layout.selectors';
import { RxjsService } from './rxjs.services';
import { SnackbarService } from './snackbar.service';
@Injectable({ providedIn: 'root' })
export class AgentLoginService {
  telephonyProvider: AgentTelephonyProvider | null = null;
  // Tracked here (rather than re-subscribed in every "click to dial" component) so
  // initiateOutboundCall() below can decide the whole flow without callers needing to know
  // or pass agentExtensionNo themselves.
  private agentExtensionNo: any = null;

  // RxjsService is resolved lazily via Injector rather than constructor-injected: this file and
  // rxjs.services.ts sit in a circular-import graph (through @app/shared / various barrel
  // index.ts files), and a directly-typed constructor parameter causes TypeScript to embed a
  // live RxjsService class reference in this class's compiled ctorParameters metadata, which
  // Angular's View Engine JIT compiler reads synchronously at class-decoration time — before
  // rxjs.services.ts has necessarily finished executing — throwing "Cannot access 'RxjsService'
  // before initialization". Injector has no relation to that cycle, so injecting it instead and
  // resolving RxjsService only when actually used (well after bootstrap) avoids the TDZ
  // entirely. Do not change this back to a direct RxjsService constructor param.
  private get rxjsService(): RxjsService {
    return this.injector.get(RxjsService);
  }

  constructor(public dialogService: DialogService, private readonly snackbarService: SnackbarService,
    private readonly injector: Injector, private readonly store: Store<AppState>) {
    this.combineLatestNgrxStore();
  }

  get isOpenScapeProvider(): boolean {
    return this.telephonyProvider === AgentTelephonyProvider.OPEN_SCAPE;
  }

  get isConnectsAiProvider(): boolean {
    return this.telephonyProvider === AgentTelephonyProvider.CONNECTS_AI;
  }

  combineLatestNgrxStore() {
    combineLatest([
      this.store.select(sidebarDataSelector$),
      this.store.select(agentLoginDataSelector),
    ])
      .subscribe((response: any) => {
        // isEnableAgent is the switch between the two CTI integrations: enabled -> ConnectsAI, disabled -> OpenScape.
        this.telephonyProvider = response[0]?.isEnableAgent ? AgentTelephonyProvider.CONNECTS_AI : AgentTelephonyProvider.OPEN_SCAPE;
        this.agentExtensionNo = response[1];
      });
  }

  openOpenscapeLoginPopup() {
    if (this.telephonyProvider === AgentTelephonyProvider.OPEN_SCAPE) {
      this.snackbarService.openSnackbar("Agent login is required", ResponseMessageTypes.WARNING);
      this.dialogService.open(ExtensionModalComponent, { width: '450px', showHeader: false });
    }
    else {
      this.rxjsService.setAnyPropertyValue({ onConnectsAIPopup: true });
    }
  }

  setCustomerDataAndExpandOpenscapeDialpad(data) {
    this.rxjsService.setCustomerContactNumber(data);
  }

  // Single place to decide how to start a customer call, reused across all "click to dial" call
  // sites instead of each one re-deriving the OpenScape-vs-connects-ai branching itself.
  // connects-ai never requires agentExtensionNo: mount the widget (openOpenscapeLoginPopup's
  // connects-ai branch) and dispatch the dial data unconditionally. OpenScape keeps its existing
  // extension-required gate. agentExtensionNo is tracked internally above, so callers only ever
  // need to pass the dial payload. Returns whether the call was actually dispatched (false means
  // the login-required popup was shown instead) so callers with post-dial-only side effects
  // (closing an unrelated dialog, unsubscribing a call-log listener, etc.) can gate on it.
  initiateOutboundCall(data: any): boolean {
    if (this.isConnectsAiProvider) {
      this.openOpenscapeLoginPopup();
      this.setCustomerDataAndExpandOpenscapeDialpad(data);
      return true;
    }
    if (!this.agentExtensionNo) {
      this.openOpenscapeLoginPopup();
      return false;
    }
    this.setCustomerDataAndExpandOpenscapeDialpad(data);
    return true;
  }

  // Whether a call could be initiated right now (connects-ai, or OpenScape with an extension) —
  // for call sites that need to know this before rendering a dial UI at all (see
  // CustomerCallModalComponent), as opposed to initiateOutboundCall's "attempt it now" behavior.
  get canInitiateOutboundCall(): boolean {
    return this.isConnectsAiProvider || !!this.agentExtensionNo;
  }
}